/**
 * @component ry-source
 * @category  data
 * @summary   Headless data provider. Holds a `fetcher` conforming to the ry-ui data
 *            contract and hands it to whichever widgets point at it by id
 *            (`<ry-table source="orders">`) — the same trigger-pairing convention as
 *            `<ry-button modal="id">`. Renders nothing. It is a provider of TRANSPORT,
 *            not an owner of request lifecycle: each consuming widget runs its own
 *            request with its own abort signal, so two widgets sharing one source don't
 *            cancel each other. `load()` exists for standalone/imperative use.
 *
 * @attribute cache-ms  number?  default=0 (off)  Milliseconds a response stays reusable. With
 *                           it set, a request whose `requestKey` matches a recent one is served
 *                           from memory instead of hitting the network. `invalidate()` clears it.
 *
 * @attribute tags     string?   Comma-separated labels this source answers to. `RySourceElement.invalidateTags('orders')` invalidates every live source carrying the tag — the cross-resource case a per-source `invalidate()` can't express.
 * @attribute timeout  number?   Abort a request after this many ms. Surfaces as `ry:load-error` with a `RyTimeoutError`, NOT as a silent abort: running out of time is a failure the user must see, unlike being superseded.
 * @attribute retry    number?   default=0   Extra attempts after a RETRYABLE failure (5xx / 408 / 429 / timeout / dead network), exponential backoff. A 404 is never retried.
 * @attribute stale-ms number?   default=0   Window past `[cache-ms]` in which a stale response is served INSTANTLY while a refresh runs behind it. The refresh landing emits `ry:invalidate`.
 * @attribute reconnect  boolean  default=false  Reopen a dropped subscription with exponential backoff, resuming from the last `resume()` token.
 * @attribute reconnect-delay      number?  default=500    First backoff step in ms; doubles per attempt. Reset by any successful patch.
 * @attribute reconnect-max-delay  number?  default=30000  Backoff ceiling, so a long outage doesn't become a five-minute wait.
 *
 * @attribute stream-field  string?  default="text"  Field an `append` chunk extends when the
 *                           chunk doesn't name one. Only relevant to `stream()`.
 *
 * @attribute meta  string?  JSON object merged into every request's `meta` (both this
 *                           element's own `load()` and any widget that reads
 *                           `sourceMeta`). Opaque — never read here, only forwarded.
 *                           Invalid JSON warns and is ignored rather than throwing.
 *
 * @property  fetcher  RySource | null   The function that actually fetches. Set it in JS —
 *                                       a function can't be expressed as an attribute.
 * @property  state    RyDataState       Lifecycle of this element's OWN `load()` calls,
 *                                       mirrored to `data-ry-state`. A widget consuming
 *                                       this source tracks its own state separately.
 * @property  rows     ReadonlyArray<RyRecord>   Rows from this element's last `load()`.
 * @property  total    number | undefined        Total from this element's last `load()`.
 * @property  sourceMeta  Record<string, unknown>   Parsed `[meta]`, for consumers to merge.
 * @property  sharedFetcher  RySource | null   What consumers actually call: `fetcher` wrapped so
 *                           identical concurrent requests share one call and cached results are
 *                           reused. Set `fetcher`; read this.
 * @property  cacheMs     number   Reads `[cache-ms]`.
 * @property  mutations   RyMutations | null   Write operations. Optional — only widgets that
 *                           mutate need them. Supplied by the consumer, like `fetcher`.
 * @property  streamField string   Reads `[stream-field]` (default "text"); the field an
 *                           `append` chunk extends when it doesn't name one.
 * @property  subscribed  boolean   True while a live connection is open.
 * @property  tags        string[]  Parsed `[tags]`.
 * @property  timeoutMs / retries / staleMs / reconnect / reconnectDelayMs / reconnectMaxDelayMs
 *                        Parsed forms of the attributes above.
 *
 * @method    load(partial?)   Promise<RyResponse | null>   Run a request from THIS element.
 *                             Aborts this element's own previous request. Resolves to the
 *                             response, or null when there's no fetcher, when `ry:load` was
 *                             cancelled, or when superseded.
 * @method    abort()          void   Cancel this element's in-flight request. Emits nothing —
 *                             an abort is not a failure.
 * @method    invalidate()     void   Emit `ry:invalidate`, telling every widget reading from
 *                             this source to reload. The cheap tier of cross-widget
 *                             reactivity — call it after a mutation.
 * @method    stream(source, init?)   Promise<rows>   Consume a streaming source into `rows`.
 *                             `append` chunks glue onto the last row's field (the LLM case);
 *                             `add` chunks are whole new rows (the log case). A mid-stream
 *                             failure KEEPS what arrived and lands in `error`.
 * @method    subscribe(source, init?)   RySubscription   Open a live connection. Patches are
 *                             applied to `rows` by `id`, so a push costs nobody their scroll
 *                             position or selection. A second call closes the first; disconnect
 *                             always closes.
 * @method    unsubscribe()    void   Close the live connection AND cancel any pending reconnect. Safe with none open.
 * @method    prefetch(init?)  Promise<void>   Warm the cache without touching state or firing events. Hover a row, prefetch the detail; the real load moments later joins the in-flight request or finds it cached.
 * @method    static invalidateTags(...tags)   number   Invalidate every live source carrying any of these tags. Returns how many matched, so a caller can tell a no-op from a hit.
 * @method    mutate(run, optimistic?)   Promise<T|null>   Run a write optimistically. `optimistic`
 *                             transforms `rows` on the spot; if `run` rejects, rows are restored
 *                             EXACTLY and `ry:mutate-error` fires. Returns null without
 *                             `mutations` set, or on failure.
 *
 * @event ry:load
 *        detail: { request: RyRequest }
 *        fires:  before this element's fetcher is called. Cancelable — `preventDefault()`
 *                skips the request and `load()` resolves to null.
 *
 * @event ry:loaded
 *        detail: { rows, total?, cursor?, request }
 *        fires:  after this element's fetcher resolves. Not fired for aborted requests.
 *
 * @event ry:load-error
 *        detail: { error, request }
 *        fires:  this element's fetcher threw or rejected. NOT fired when the request was
 *                aborted — superseding a request is normal operation, not a failure.
 *                Named `ry:load-error` rather than `ry:error` because `ry-copy-button`
 *                already owns `ry:error`.
 *
 * @event ry:invalidate
 *        detail: {}
 *        fires:  `invalidate()` was called. Every widget pointing at this source reloads,
 *                including ones currently showing server-rendered rows.
 *
 * @event ry:chunk
 *        detail: { chunk, rows }
 *        fires:  once per streamed chunk, after it has been applied. `rows` is the state so far.
 *
 * @event ry:patch
 *        detail: { patch, rows }
 *        fires:  once per subscription patch, after it has been applied.
 *
 * @event ry:mutate-error
 *        detail: { error }
 *        fires:  a `mutate()` run rejected AND rows were reverted. Not fired on success.
 *
 * @event ry:subscribe-closed
 *        detail: { error?, willRetry, delay?, attempt? }
 *        fires:  a subscription closed. `willRetry` says whether a reconnect is scheduled —
 *                without this event a dropped socket just looks connected forever.
 *
 * @event ry:source-ready
 *        detail: {}
 *        fires:  the first time `fetcher` is assigned a non-null value. Widgets that
 *                resolved this source before its fetcher existed listen for it so they
 *                don't depend on script execution order. Assigning a second fetcher does
 *                NOT re-fire it.
 *
 * @state     `data-ry-state`: 'idle' → 'loading' → 'ready' | 'empty' | 'error'. Reflects
 *            this element's own `load()` only.
 *
 * @content   None. This element renders nothing and has no children.
 *
 * @data      provider — holds the fetcher; supplies rows to widgets via [source]
 *            emits: nothing on its own; each consuming widget decides range / sort / filter
 *
 * @dom
 *   ry-source[meta?]   hidden   aria-hidden="true"   data-ry-state="idle|loading|ready|empty|error"
 *     (no children — the element renders nothing)
 *
 * @gotcha  This element does NOT own the request lifecycle for its consumers — each widget
 *          keeps its own abort signal so they can't cancel each other. What IS shared is the
 *          request itself: two widgets asking the identical question (same `requestKey`) get
 *          ONE network call. The shared call has its own signal and is ref-counted, so one
 *          widget re-sorting doesn't abort the fetch another is still waiting on.
 *
 * @gotcha  Deduping only applies to IDENTICAL requests. A table on page 3 sorted by date and
 *          a chart wanting everything unsorted share no result set and will each fetch — that's
 *          correct, not a miss. Sharing is keyed on the question, not on the source.
 *
 * @gotcha  `stream()`, `subscribe()` and `mutate()` exist here as the REFERENCE consumer for
 *          shapes no component uses yet (chat, live dashboards, kanban). They're real and
 *          tested, so those components inherit tested behavior instead of each writing their
 *          own — see docs/arch/data-contract-patterns.md.
 *
 * @gotcha  A new `load()` ABORTS this element's previous one. The superseded call resolves
 *          to `null` and fires no `ry:load-error`.
 *
 * @gotcha  `fetcher` is a PROPERTY, not an attribute. `<ry-source fetcher="...">` does
 *          nothing.
 *
 * @gotcha  There is no `src` attribute yet — you supply the transport. A built-in REST
 *          fetcher (`<ry-source src="/api/orders">`) is scheduled once the contract is
 *          proven against a widget; see docs/plans/data-contract-rollout.md.
 *
 * @gotcha  `<ry-source>` is deliberately NOT in the `<ry>` wrapper's transform list, because
 *          `<source>` is a real HTML element inside `<video>` / `<audio>` / `<picture>`.
 *          Write the prefixed tag even inside `<ry>`.
 *
 * @example basic
 * ```html
 * <ry-source id="orders"></ry-source>
 * <script type="module">
 *   document.getElementById('orders').fetcher = async ({ range, sort, signal }) => {
 *     const res = await fetch('/api/orders', { signal });
 *     return { rows: await res.json(), total: 120 };
 *   };
 * </script>
 * ```
 *
 * @example with-meta
 * ```html
 * <ry-source id="tenant-orders" meta='{"tenant":"acme"}'></ry-source>
 * ```
 *
 * @tutorial basic-interaction
 * ```js
 * const el = mount('<ry-source id="s1"></ry-source>');
 * el.fetcher = () => ({ rows: [{ id: 1, name: 'a' }], total: 1 });
 * const res = await el.load();
 * await expectEqual(res.rows.length, 1);
 * await expectEqual(el.rows.length, 1, 'rows cached on the element');
 * await expectEqual(el.total, 1);
 * await expectAttr(el, 'data-ry-state', 'ready');
 * ```
 *
 * @tutorial empty-is-its-own-state
 * ```js
 * // Zero rows is a SUCCESS, not an error — it gets its own state so a widget
 * // can render an empty slot instead of a failure message.
 * const el = mount('<ry-source id="s2"></ry-source>');
 * el.fetcher = () => ({ rows: [] });
 * await el.load();
 * await expectAttr(el, 'data-ry-state', 'empty');
 * ```
 *
 * @tutorial programmatic-api
 * ```js
 * // load(partial) merges into the request; the fetcher receives it verbatim.
 * const el = mount('<ry-source id="s3"></ry-source>');
 * let seen = null;
 * el.fetcher = (req) => { seen = req; return { rows: [] }; };
 * await el.load({ range: { start: 20, end: 40 }, sort: [{ field: 'date', dir: 'desc' }] });
 * await expectEqual(seen.range.start, 20);
 * await expectEqual(seen.sort[0].field, 'date');
 * await expectEqual(typeof seen.signal, 'object', 'signal is always present');
 * ```
 *
 * @tutorial errors-land-in-the-error-state
 * ```js
 * const el = mount('<ry-source id="s4"></ry-source>');
 * el.fetcher = () => { throw new Error('boom'); };
 * const details = [];
 * el.addEventListener('ry:load-error', (e) => details.push(e.detail));
 * const res = await el.load();
 * await expectEqual(res, null, 'load() resolves null on failure');
 * await expectAttr(el, 'data-ry-state', 'error');
 * await expectEqual(details.length, 1);
 * ```
 *
 * @tutorial wrong-assumption
 * ```js
 * // A model trained on fetch wrappers assumes concurrent loads all resolve.
 * // They don't: a new load() ABORTS the previous one, which resolves to null
 * // and fires NO ry:load-error. That's deliberate — superseding a request is
 * // normal operation, and treating it as a failure is how stale rows get
 * // rendered over fresh ones.
 * const el = mount('<ry-source id="s5"></ry-source>');
 * let call = 0;
 * el.fetcher = (req) => {
 *   call++;
 *   if (call === 1) {
 *     return new Promise((resolve) => {
 *       req.signal.addEventListener('abort', () => resolve({ rows: [{ id: 'stale' }] }));
 *     });
 *   }
 *   return { rows: [{ id: 'fresh' }] };
 * };
 * const errors = [];
 * el.addEventListener('ry:load-error', () => errors.push(1));
 * const first = el.load();
 * const second = el.load();          // supersedes the first
 * await expectEqual(await first, null, 'superseded load resolves null');
 * await expectEqual((await second).rows[0].id, 'fresh');
 * await expectEqual(el.rows[0].id, 'fresh', 'stale rows never land');
 * await expectEqual(errors.length, 0, 'an abort is not an error');
 * ```
 *
 * @tutorial ry-load-is-cancelable
 * ```js
 * // preventDefault() on ry:load skips the request entirely — the fetcher is
 * // never called.
 * const el = mount('<ry-source id="s6"></ry-source>');
 * let called = 0;
 * el.fetcher = () => { called++; return { rows: [] }; };
 * el.addEventListener('ry:load', (e) => e.preventDefault());
 * const res = await el.load();
 * await expectEqual(res, null);
 * await expectEqual(called, 0, 'fetcher never ran');
 * await expectAttr(el, 'data-ry-state', 'idle', 'state unchanged');
 * ```
 *
 * @see ry-table
 * @see ry-pagination
 */

import { RyElement } from '../core/ry-element.js';
import { RyLoaderRunner, type RyRequestInit } from '../core/ry-loader-runner.js';
import { RyCoalescer } from '../core/ry-coalesce.js';
import { isAbort } from '../core/ry-data.js';
import { applyChunk, applyPatch } from '../core/ry-rows.js';
import type {
  RyChunkDetail, RyDataState, RyMutateErrorDetail, RyMutations, RyPatchDetail,
  RyRecord, RyRequest, RyResponse, RySource, RyStreamSource, RySubscribeSource,
  RySubscribeHandlers, RySubscription,
} from '../core/ry-data.js';

export type { RyRequestInit };

/**
 * Every connected <ry-source>. Module-level rather than a store: the set is the
 * whole state, and tag invalidation is the one operation that legitimately
 * crosses source boundaries. This is what people reach for Redux to get, at
 * roughly thirty lines and with no reducers.
 */
const LIVE_SOURCES = new Set<RySourceElement>();

export class RySourceElement extends RyElement {
  #fetcher: RySource | null = null;
  #runner = new RyLoaderRunner(this, {
    baseMeta: () => this.sourceMeta,
    timeoutMs: () => this.timeoutMs,
    retries: () => this.retries,
  });
  #coalescer = new RyCoalescer({
    cacheMs: () => this.cacheMs,
    staleMs: () => this.staleMs,
    // A background refresh that lands is news every reader wants.
    onRevalidated: () => this.emit('invalidate', {}),
  });
  #shared: RySource | null = null;
  #subscription: RySubscription | null = null;
  #streamController: AbortController | null = null;

  /**
   * Write operations. Optional — only a widget that mutates needs them.
   * Supplied by the consumer, same as `fetcher`.
   */
  mutations: RyMutations | null = null;
  #rows: ReadonlyArray<RyRecord> = [];
  #total: number | undefined = undefined;

  setup(): void {
    LIVE_SOURCES.add(this);
    // Headless: never rendered, never in the a11y tree, never focusable.
    this.hidden = true;
    this.setAttribute('aria-hidden', 'true');
    RyLoaderRunner.initState(this);
  }

  teardown(): void {
    LIVE_SOURCES.delete(this);
    this.abort();
    this.unsubscribe();
    this.#streamController?.abort();
    this.#streamController = null;
  }

  /**
   * Set this in JS. A function can't be expressed as an attribute.
   *
   * Assigning it emits `ry:source-ready`. Widgets resolve their `[source]` at
   * mount, which usually happens BEFORE the consumer's script has assigned a
   * fetcher — module execution order isn't something an author should have to
   * reason about. So a widget that finds a fetcher-less source waits for this
   * event instead of silently staying idle.
   */
  get fetcher(): RySource | null {
    return this.#fetcher;
  }

  set fetcher(value: RySource | null) {
    const had = this.#fetcher !== null;
    this.#fetcher = value;
    // A new fetcher invalidates everything the old one produced.
    this.#shared = value ? this.#coalescer.wrap(value) : null;
    this.#coalescer.clear();
    if (value && !had) this.emit('source-ready', {});
  }

  /**
   * The fetcher consumers actually call. Same contract as `fetcher`, wrapped so
   * identical concurrent requests share one call and (with `[cache-ms]`) recent
   * results are reused. Null when no fetcher is set.
   */
  get sharedFetcher(): RySource | null {
    return this.#shared;
  }

  /** Milliseconds a response stays reusable. 0 (default) means no caching. */
  get cacheMs(): number {
    const raw = Number(this.getAttribute('cache-ms'));
    return Number.isFinite(raw) && raw > 0 ? raw : 0;
  }

  /**
   * Extra window past `cache-ms` in which a stale response is served instantly
   * while a refresh runs behind it. 0 (default) = off.
   */
  get staleMs(): number {
    const raw = Number(this.getAttribute('stale-ms'));
    return Number.isFinite(raw) && raw > 0 ? raw : 0;
  }

  get rows(): ReadonlyArray<RyRecord> {
    return this.#rows;
  }

  get total(): number | undefined {
    return this.#total;
  }

  /** Cancel this element's in-flight request. Not an error — emits nothing. */
  abort(): void {
    this.#runner.abort();
  }

  /**
   * Tell every widget reading from this source that its rows are stale.
   *
   * This is the cheap tier of cross-widget reactivity: no cache, no request
   * keys, no shared store. Call it after a mutation and every consumer reloads
   * — including ones showing server-rendered rows, which is the escape hatch
   * for that staleness tradeoff.
   */
  /** Abort a request after this many ms. 0 (default) = no limit. */
  get timeoutMs(): number {
    const raw = Number(this.getAttribute('timeout'));
    return Number.isFinite(raw) && raw > 0 ? raw : 0;
  }

  /** Extra attempts after a RETRYABLE failure. 0 (default) = none. */
  get retries(): number {
    const raw = Number(this.getAttribute('retry'));
    return Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 0;
  }

  /** Tags this source answers to, from `[tags="orders,revenue"]`. */
  get tags(): string[] {
    return (this.getAttribute('tags') ?? '')
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
  }

  /**
   * Invalidate every LIVE source carrying any of these tags.
   *
   * The cross-resource case: deleting an order should refresh the orders table,
   * the revenue chart, and the stats tile — which may be three different
   * sources. Per-source `invalidate()` can't express that, and making every
   * caller remember the full list is exactly what people get wrong.
   *
   * Returns how many sources matched, so a caller can tell a no-op from a hit.
   */
  static invalidateTags(...tags: string[]): number {
    const wanted = new Set(tags.flatMap((t) => t.split(',').map((x) => x.trim())).filter(Boolean));
    if (wanted.size === 0) return 0;
    let hits = 0;
    for (const source of LIVE_SOURCES) {
      if (source.tags.some((t) => wanted.has(t))) {
        source.invalidate();
        hits++;
      }
    }
    return hits;
  }

  /**
   * Warm the cache without touching state or firing lifecycle events.
   *
   * Hover a row, prefetch the detail. Free given coalescing already exists: the
   * real load moments later finds the request in flight or cached and joins it.
   * Deliberately silent — a prefetch that flipped the widget to `loading` would
   * defeat its own purpose.
   */
  async prefetch(init: Partial<RyRequestInit> = {}): Promise<void> {
    if (!this.#shared) return;
    const controller = new AbortController();
    try {
      await this.#shared({
        ...init,
        meta: { ...this.sourceMeta, ...(init.meta ?? {}) },
        signal: controller.signal,
      });
    } catch {
      // A prefetch that fails is not an event. The real load will report it.
    }
  }

  invalidate(): void {
    // Drop cached responses BEFORE telling anyone, or every consumer reloads
    // straight back into the stale cache and nothing changes.
    this.#coalescer.clear();
    this.emit('invalidate', {});
  }

  async load(init: Partial<RyRequestInit> = {}): Promise<RyResponse | null> {
    if (!this.#shared) return null;
    const response = await this.#runner.run(this.#shared, init);
    if (response) {
      this.#rows = response.rows;
      this.#total = response.total;
    }
    return response;
  }

  // ────────────────────────────────────────────────────────────────
  // Stream  (docs/arch/data-contract-patterns.md — the STREAM shape)
  // ────────────────────────────────────────────────────────────────

  /**
   * Consume a streaming source into `rows`, chunk by chunk.
   *
   * `append` chunks glue onto the last row's field — the LLM case — via the
   * shared `applyChunk`, so a real chat component inherits the same behavior
   * rather than reimplementing token merging.
   *
   * Mid-stream failure KEEPS what already arrived and lands in `error`.
   * Discarding a half-written answer is never what a chat user wants.
   */
  async stream(
    source: RyStreamSource,
    init: Partial<RyRequestInit> = {}
  ): Promise<ReadonlyArray<RyRecord>> {
    this.#streamController?.abort();
    const controller = new AbortController();
    this.#streamController = controller;

    const request: RyRequest = {
      ...init,
      meta: { ...this.sourceMeta, ...(init.meta ?? {}) },
      signal: controller.signal,
    };

    this.#rows = [];
    this.state = 'loading';
    this.setAttribute('aria-busy', 'true');

    try {
      for await (const chunk of source(request)) {
        if (controller.signal.aborted) break;
        this.#rows = applyChunk(this.#rows, chunk, this.streamField);
        this.emit<RyChunkDetail>('chunk', { chunk, rows: this.#rows });
        if (chunk.op === 'done') break;
      }
      if (!controller.signal.aborted) {
        this.removeAttribute('aria-busy');
        this.state = this.#rows.length === 0 ? 'empty' : 'ready';
      }
    } catch (error) {
      if (!controller.signal.aborted && !isAbort(error)) {
        this.removeAttribute('aria-busy');
        // Partial content stays on purpose.
        this.state = 'error';
        this.emit('load-error', { error, request });
      }
    } finally {
      if (this.#streamController === controller) this.#streamController = null;
    }
    return this.#rows;
  }

  /** Field an `append` chunk extends when it doesn't name one. */
  get streamField(): string {
    return this.getAttribute('stream-field') ?? 'text';
  }

  // ────────────────────────────────────────────────────────────────
  // Subscribe  (the SUBSCRIBE shape)
  // ────────────────────────────────────────────────────────────────

  /**
   * Open a live connection. Patches are applied to `rows` in place, matched on
   * `id`, so a push costs nobody their scroll position or selection.
   *
   * Idempotent-ish: opening a second subscription closes the first. Always
   * closed on disconnect — a component that leaks a socket is a bug.
   */
  subscribe(source: RySubscribeSource, init: Partial<RyRequestInit> = {}): RySubscription {
    this.unsubscribe();

    let stopped = false;
    let attempt = 0;
    let resumeToken: string | undefined = init.cursor;
    let inner: RySubscription | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let controller = new AbortController();

    const open = (): void => {
      if (stopped) return;
      controller = new AbortController();
      const request: RyRequest = {
        ...init,
        cursor: resumeToken,
        meta: { ...this.sourceMeta, ...(init.meta ?? {}) },
        signal: controller.signal,
      };

      const handlers: RySubscribeHandlers = {
        patch: (patch) => {
          if (stopped) return;
          // A patch is proof the connection works, so the next drop starts
          // backing off from zero again rather than from where we left off.
          attempt = 0;
          this.#rows = applyPatch(this.#rows, patch);
          if (this.state !== 'ready') this.state = this.#rows.length ? 'ready' : 'empty';
          this.emit<RyPatchDetail>('patch', { patch, rows: this.#rows });
        },
        closed: (error) => {
          if (stopped) return;
          inner = null;
          if (!this.reconnect) {
            this.state = error ? 'error' : this.state;
            this.emit('subscribe-closed', { error, willRetry: false });
            return;
          }
          const delay = Math.min(
            this.reconnectDelayMs * 2 ** attempt,
            this.reconnectMaxDelayMs
          );
          attempt++;
          this.emit('subscribe-closed', { error, willRetry: true, delay, attempt });
          retryTimer = setTimeout(open, delay);
        },
        resume: (token) => { resumeToken = token; },
      };

      inner = source(request, handlers);
    };

    open();

    this.#subscription = {
      unsubscribe: () => {
        stopped = true;
        if (retryTimer !== null) clearTimeout(retryTimer);
        retryTimer = null;
        controller.abort();
        inner?.unsubscribe();
        inner = null;
      },
    };
    return this.#subscription;
  }

  /**
   * Close the live connection and stop any pending reconnect. Safe to call when
   * there isn't one.
   */
  unsubscribe(): void {
    const sub = this.#subscription;
    this.#subscription = null;
    sub?.unsubscribe();
  }

  /** Reconnect after an unexpected close. Off unless `[reconnect]` is present. */
  get reconnect(): boolean {
    return this.hasAttribute('reconnect');
  }

  /** First backoff step in ms; doubles per attempt. */
  get reconnectDelayMs(): number {
    const raw = Number(this.getAttribute('reconnect-delay'));
    return Number.isFinite(raw) && raw > 0 ? raw : 500;
  }

  /** Backoff ceiling, so a long outage doesn't become a five-minute wait. */
  get reconnectMaxDelayMs(): number {
    const raw = Number(this.getAttribute('reconnect-max-delay'));
    return Number.isFinite(raw) && raw > 0 ? raw : 30000;
  }

  get subscribed(): boolean {
    return this.#subscription !== null;
  }

  // ────────────────────────────────────────────────────────────────
  // Mutate  (the MUTATE shape)
  // ────────────────────────────────────────────────────────────────

  /**
   * Run a write optimistically.
   *
   * `optimistic` transforms `rows` immediately so the UI responds on the same
   * frame the user acted. If `run` rejects, rows are restored EXACTLY as they
   * were and `ry:mutate-error` fires. That revert is the whole reason this
   * lives in the library: remembering the previous state is the part every
   * consumer would otherwise get wrong.
   */
  async mutate<T>(
    run: (mutations: RyMutations, req: RyRequest) => Promise<T>,
    optimistic?: (rows: ReadonlyArray<RyRecord>) => ReadonlyArray<RyRecord>
  ): Promise<T | null> {
    if (!this.mutations) return null;
    const snapshot = this.#rows;
    const controller = new AbortController();
    const request: RyRequest = { meta: this.sourceMeta, signal: controller.signal };

    if (optimistic) {
      this.#rows = optimistic(this.#rows);
      this.state = this.#rows.length === 0 ? 'empty' : 'ready';
    }

    try {
      const result = await run(this.mutations, request);
      // A successful write invalidates anything cached from before it.
      this.#coalescer.clear();
      return result;
    } catch (error) {
      this.#rows = snapshot;                       // exact restore
      this.state = snapshot.length === 0 ? 'empty' : 'ready';
      this.emit<RyMutateErrorDetail>('mutate-error', { error });
      return null;
    }
  }

  /**
   * Parsed `[meta]`, for consumers to merge into their own requests. Malformed
   * JSON warns and yields `{}` — a bad attribute shouldn't take the page down.
   */
  get sourceMeta(): Record<string, unknown> {
    const raw = this.getAttribute('meta');
    if (!raw) return {};
    try {
      const parsed: unknown = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
      console.warn('[ry-source] [meta] must be a JSON object; ignoring:', raw);
    } catch {
      console.warn('[ry-source] [meta] is not valid JSON; ignoring:', raw);
    }
    return {};
  }

  /** `state` is narrowed here; RyElement's base getter/setter is a plain string. */
  override get state(): RyDataState {
    return (this.dataset.ryState as RyDataState | undefined) ?? 'idle';
  }

  override set state(value: RyDataState) {
    super.state = value;
  }
}

/** What a widget gets back when it resolves its `source`. */
export interface ResolvedSource {
  /** Call this to fetch. Null when the provider exists but has no fetcher YET. */
  fetcher: RySource | null;
  /** `[meta]` from the provider element, if the source was one. */
  baseMeta: Record<string, unknown>;
  /** The provider element, when the source came from one. Null for a bare function. */
  element: RySourceElement | null;
}

/**
 * Resolve a widget's `.source` property or `source="id"` attribute into ONE
 * normalized shape. Returns null when there's no source — which is how a widget
 * knows it's in DOM-as-truth mode.
 *
 * Returning a normalized object rather than a union is deliberate: every
 * consumer would otherwise repeat the same `instanceof` narrowing, and the
 * id-pointing convention should be implemented exactly once.
 */
export function resolveSource(host: HTMLElement): ResolvedSource | null {
  const direct = (host as HTMLElement & { source?: RySource | RySourceElement | null }).source;
  if (typeof direct === 'function') {
    return { fetcher: direct, baseMeta: {}, element: null };
  }
  const el =
    direct instanceof RySourceElement
      ? direct
      : (() => {
          const id = host.getAttribute('source');
          if (!id) return null;
          const found = host.ownerDocument.getElementById(id);
          return found instanceof RySourceElement ? found : null;
        })();

  if (!el) return null;
  // A fetcher-less source still RESOLVES — the widget needs the element so it
  // can wait for `ry:source-ready`. `fetcher: null` means "not yet", which is
  // different from "no source", and only the widget can tell those apart.
  //
  // Consumers get the SHARED fetcher, so dedupe and caching apply without any
  // widget knowing they exist.
  return { fetcher: el.sharedFetcher, baseMeta: el.sourceMeta, element: el };
}

customElements.define('ry-source', RySourceElement);
